import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { apiService } from '@/services/api'
import { cn } from '@/lib/utils'
import type { Character } from '@/types'

/**
 * Mestre pede um teste (fase 3 de docs/PLANO_TESTE_ATRIBUTO.md).
 *
 * Abre o modal de dados na mesa inteira com o teste já definido: atributo,
 * dificuldade (visível ao aluno antes de rolar — decisão do Paulo) e bônus
 * situacional. Quem rola é só o convocado; o servidor guarda o pedido na
 * sessão para o aluno não conseguir trocar nada.
 *
 * Duas maneiras de dizer a quem o teste cabe:
 *
 * - **pela ficha** (`character`): o mestre aponta o jogador na ficha dele;
 * - **pela vez** (`alvoDaVez`): o pedido sai sem alvo e o SERVIDOR convoca quem
 *   tem a vez — fase 1 de docs/PLANO_ROLAGEM_CLASSICA.md. O nome aqui é só
 *   para a tela poder dizer de quem é a vez; quem decide é a fila do turno.
 */

interface PedirTesteDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  tableId: string
  /** Personagem do jogador convocado (a ficha que será testada) */
  character: Character | null
  /**
   * Nome de quem tem a vez. Presente (e `character` ausente) = pedido pela vez.
   * Não vai na requisição: o servidor resolve a vez de novo, na hora.
   */
  alvoDaVez?: string | null
}

interface AtributoDoJogo {
  id: string
  name: string
}

/**
 * Vantagem / desvantagem (fase 2 de docs/PLANO_ROLAGEM_CLASSICA.md).
 *
 * É a forma do modificador situacional que não pede aritmética: em vez de
 * "+2", "você se preparou bem — role com vantagem". Rola dois dados e vale o
 * melhor (ou o pior). O campo Bônus continua existindo, para quem quiser o
 * número; os dois convivem.
 */
const MODOS = [
  { id: '', rotulo: 'Normal', dica: 'Um dado' },
  { id: 'vantagem', rotulo: 'Vantagem', dica: 'Dois dados, vale o maior' },
  { id: 'desvantagem', rotulo: 'Desvantagem', dica: 'Dois dados, vale o menor' },
] as const

export function PedirTesteDialog({
  open,
  onOpenChange,
  tableId,
  character,
  alvoDaVez,
}: PedirTesteDialogProps) {
  // Quem a tela diz que vai rolar. Com `character`, é o dono da ficha; pela
  // vez, o nome que veio da fila do turno.
  const nomeDoAlvo = character?.name ?? alvoDaVez ?? null
  const temAlvo = Boolean(character || alvoDaVez)
  const [atributos, setAtributos] = useState<AtributoDoJogo[]>([])
  const [atributoId, setAtributoId] = useState('')
  // O que está sendo tentado. Primeiro campo da tela de propósito: na cabeça
  // do mestre a AÇÃO vem antes do atributo — ele decide "ela quer arrombar a
  // porta" e só então escolhe por qual atributo isso se resolve.
  const [acao, setAcao] = useState('')
  const [dificuldade, setDificuldade] = useState('')
  const [bonus, setBonus] = useState('')
  const [modo, setModo] = useState<string>('')
  const [enviando, setEnviando] = useState(false)

  useEffect(() => {
    if (!open || !tableId) return
    apiService.getGameConfig(tableId)
      .then((config: { attributes?: AtributoDoJogo[] }) => {
        const lista = config?.attributes ?? []
        setAtributos(lista)
        setAtributoId((atual) => atual || lista[0]?.id || '')
      })
      .catch(() => setAtributos([]))
  }, [open, tableId])

  useEffect(() => {
    if (!open) {
      setAcao('')
      setDificuldade('')
      setBonus('')
      setModo('')
    }
  }, [open])

  const pedir = async () => {
    if (!temAlvo || !atributoId) return
    setEnviando(true)
    try {
      await apiService.request('/dice/modal/open', {
        method: 'POST',
        body: JSON.stringify({
          table_id: tableId,
          dice_type: 'D20',
          // Sem alvo, o servidor convoca quem tem a vez. Mandar o nome que a
          // tela mostra seria pior que não mandar nada: a vez pode ter virado
          // entre abrir este diálogo e tocar em "Pedir teste".
          ...(character ? { target_user_id: character.userId } : {}),
          attribute_id: atributoId,
          // Só vai quando há o que mandar: vazio e ausente significam a mesma
          // coisa, e o servidor normaliza de novo do lado de lá.
          ...(acao.trim() ? { action_name: acao.trim() } : {}),
          ...(dificuldade ? { difficulty: Number(dificuldade) } : {}),
          ...(bonus ? { bonus: Number(bonus) } : {}),
          ...(modo ? { advantage: modo } : {}),
        }),
      })
      const nome = atributos.find(a => a.id === atributoId)?.name ?? 'atributo'
      toast.success(`Teste de ${nome} pedido a ${nomeDoAlvo}`)
      onOpenChange(false)
    } catch (erro: any) {
      toast.error(erro?.message || 'Não foi possível pedir o teste')
    } finally {
      setEnviando(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-game-secondary border-game-primary max-w-sm">
        <DialogHeader>
          <DialogTitle className="text-game-primary">
            Pedir teste {nomeDoAlvo ? `a ${nomeDoAlvo}` : ''}
          </DialogTitle>
          <DialogDescription className="text-game-secondary text-xs">
            O modal abre para a mesa toda, mas só {nomeDoAlvo ?? 'o convocado'} rola.
            A dificuldade fica à vista antes da rolagem.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="acao" className="text-game-primary">
              O que está sendo tentado{' '}
              <span className="text-game-muted font-normal">(opcional)</span>
            </Label>
            <Input
              id="acao"
              maxLength={120}
              placeholder="arrombar a porta"
              value={acao}
              onChange={(e) => setAcao(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label className="text-game-primary">Atributo</Label>
            <div className="grid grid-cols-2 gap-2">
              {atributos.map((atributo) => (
                <Button
                  key={atributo.id}
                  type="button"
                  variant="ghost"
                  onClick={() => setAtributoId(atributo.id)}
                  aria-pressed={atributoId === atributo.id}
                  className={cn(
                    'h-11 border border-game-primary rounded-xl justify-center',
                    atributoId === atributo.id
                      ? 'bg-amber-500/20 text-amber-400 ring-2 ring-amber-400/60'
                      : 'text-game-primary dark:text-white'
                  )}
                >
                  {atributo.name}
                </Button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="dificuldade" className="text-game-primary">
                Dificuldade
              </Label>
              <Input
                id="dificuldade"
                type="number"
                inputMode="numeric"
                min={1}
                max={100}
                placeholder="sem"
                value={dificuldade}
                onChange={(e) => setDificuldade(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="bonus" className="text-game-primary">
                Bônus
              </Label>
              <Input
                id="bonus"
                type="number"
                inputMode="numeric"
                min={-100}
                max={100}
                placeholder="0"
                value={bonus}
                onChange={(e) => setBonus(e.target.value)}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-game-primary">Como rolar</Label>
            <div className="grid grid-cols-3 gap-2">
              {MODOS.map((opcao) => (
                <Button
                  key={opcao.id || 'normal'}
                  type="button"
                  variant="ghost"
                  onClick={() => setModo(opcao.id)}
                  aria-pressed={modo === opcao.id}
                  title={opcao.dica}
                  className={cn(
                    'h-11 border border-game-primary rounded-xl justify-center text-xs',
                    modo === opcao.id
                      ? 'bg-amber-500/20 text-amber-400 ring-2 ring-amber-400/60'
                      : 'text-game-primary dark:text-white'
                  )}
                >
                  {opcao.rotulo}
                </Button>
              ))}
            </div>
            {modo && (
              <p className="text-game-muted text-xs">
                {MODOS.find(m => m.id === modo)?.dica}.
              </p>
            )}
          </div>

          <p className="text-game-muted text-xs">
            Sem dificuldade, o teste mostra só o total — sem veredito.
          </p>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={enviando}>
            Cancelar
          </Button>
          <Button
            onClick={pedir}
            disabled={enviando || !atributoId || !temAlvo}
            className="bg-amber-500/20 text-amber-400 hover:bg-amber-500/30"
          >
            {enviando ? 'Pedindo...' : 'Pedir teste'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
